import { assertTokenValidForMutation } from "@/services/service-helpers";

const apiUrl = import.meta.env.VITE_NBH_URL_ROOT;

export type SqlExportFormat = 'full' | 'structure' | 'data';

export async function downloadExcelExport(token: string): Promise<void> {
    const response = await fetch(`${apiUrl}/api/admin/exports/excel`, {
        headers: { Authorization: `Bearer ${token}` },
    });

    if (!response.ok) {
        throw new Error(`Export failed: ${response.statusText}`);
    }

    const blob = await response.blob();
    downloadBlob(blob, getFilenameFromResponse(response) || generateFilename('vollstaendiger-export', 'xlsx'));
}

export async function downloadSqlExport(token: string, format: SqlExportFormat = 'full'): Promise<void> {
    const response = await fetch(`${apiUrl}/api/admin/exports/sql?format=${format}`, {
        headers: { Authorization: `Bearer ${token}` },
    });

    if (!response.ok) {
        throw new Error(`Export failed: ${response.statusText}`);
    }

    const blob = await response.blob();
    downloadBlob(blob, getFilenameFromResponse(response) || generateFilename(`sql-export-${format}`, 'sql'));
}

export type SendExportsByEmailRequest = {
    includeExcel: boolean;
    includeSql: boolean;
    sqlFormat?: SqlExportFormat;
};

export type SendExportsByEmailErrorCode =
    | 'RECIPIENT_EMAIL_MISSING'
    | 'EMAIL_DISABLED'
    | 'EMAIL_SEND_FAILED'
    | 'INVALID_REQUEST'
    | 'UNKNOWN';

export class SendExportsByEmailError extends Error {
    constructor(public code: SendExportsByEmailErrorCode, message: string) {
        super(message);
        this.name = 'SendExportsByEmailError';
    }
}

export async function sendExportsByEmail(token: string, request: SendExportsByEmailRequest): Promise<void> {
    assertTokenValidForMutation()
    const response = await fetch(`${apiUrl}/api/admin/exports/email`, {
        method: 'POST',
        headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(request),
    });

    if (response.ok) return;

    let code: SendExportsByEmailErrorCode = 'UNKNOWN';
    let message = response.statusText;
    try {
        const payload = await response.json();
        if (payload && typeof payload.error === 'string') {
            code = payload.error as SendExportsByEmailErrorCode;
        }
        if (payload && typeof payload.message === 'string') {
            message = payload.message;
        }
    } catch {
        // ignore
    }
    throw new SendExportsByEmailError(code, message);
}

function downloadBlob(blob: Blob, filename: string): void {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
}

function getFilenameFromResponse(response: Response): string | null {
    const disposition = response.headers.get('Content-Disposition');
    if (!disposition) return null;
    const match = disposition.match(/filename="?([^"]+)"?/);
    return match ? match[1] : null;
}

function generateFilename(name: string, extension: string): string {
    const date = new Date().toISOString().slice(0, 10).replace(/-/g, '');
    return `${date}-${name}.${extension}`;
}
